import type { CountryLanguages } from '~/types/countryLanguages';
import { buildLocalizedPath } from '~/utils/buildLocalizedPath';

export type StorySlugData = {
    default_full_slug?: string | null;
    full_slug?: string | null;
    translated_slugs?: Array<{
        lang?: string;
        path?: string | null;
        published?: boolean | null;
    }> | null;
};

function parseStoryPath(path: string, countryLanguages: CountryLanguages) {
    const segments = path.replace(/^\/+|\/+$/g, '').split('/');
    const [firstSegment, secondSegment] = segments;
    const resolveCountry = (segment: string | undefined) => {
        if (!segment) return undefined;
        if (countryLanguages[segment]) return segment;

        const countriesWithDefaultLanguage = Object.entries(countryLanguages)
            .filter(([, config]) => config.defaultLanguage === segment)
            .map(([country]) => country);

        return countriesWithDefaultLanguage.length === 1 ? countriesWithDefaultLanguage[0] : undefined;
    };

    const prefixedCountry = resolveCountry(secondSegment);
    const knownLanguages = new Set(Object.values(countryLanguages).flatMap((config) => config.availableLanguages));

    if (firstSegment && prefixedCountry && knownLanguages.has(firstSegment)) {
        return { country: prefixedCountry, languagePrefix: firstSegment, pathSegments: segments.slice(2) };
    }

    return {
        country: resolveCountry(firstSegment) ?? firstSegment,
        languagePrefix: undefined,
        pathSegments: segments.slice(1)
    };
}

export function getStoryCountry(story: StorySlugData, countryLanguages: CountryLanguages): string {
    const defaultPath = story.default_full_slug ?? story.full_slug;
    const { country } = defaultPath ? parseStoryPath(defaultPath, countryLanguages) : { country: undefined };

    if (!country || !defaultPath || (!defaultPath.includes('/') && !countryLanguages[country])) {
        throw new Error(`Could not extract country from story path: ${defaultPath ?? ''}`);
    }

    return country;
}

export function resolveStoryPath(
    story: StorySlugData,
    language: string,
    countryLanguages: CountryLanguages
): string | null {
    const country = getStoryCountry(story, countryLanguages);
    const countryConfig = countryLanguages[country];

    if (!countryConfig) {
        throw new Error(`Country not found in countryLanguages: ${country}`);
    }
    if (!countryConfig.availableLanguages.includes(language)) return null;

    const translatedPath = story.translated_slugs?.find(
        (translatedSlug) =>
            translatedSlug.lang === language && Boolean(translatedSlug.path) && translatedSlug.published !== false
    )?.path;
    const fullSlugLanguage = story.full_slug
        ? parseStoryPath(story.full_slug, countryLanguages).languagePrefix
        : undefined;
    let path: string | null | undefined;

    if (translatedPath) {
        path = translatedPath;
    } else if (language === countryConfig.defaultLanguage) {
        path = story.default_full_slug || story.full_slug;
    } else if (fullSlugLanguage === language) {
        path = story.full_slug;
    } else {
        path = null;
    }

    if (!path) return null;

    const { country: pathCountry, pathSegments } = parseStoryPath(path, countryLanguages);
    if (pathCountry !== country) return null;

    return buildLocalizedPath(country, language, pathSegments.join('/'));
}
